import { useMemo } from "react";
import { useClientHCPLandscapeConfiguration } from "../../data/global/useProductConfiguration";
import { HcpLandscapeRow } from "../../generate";

export interface HcpColumnVisibilityResult {
  visibleColumns: (keyof HcpLandscapeRow | "full_name")[];
  isColumnVisible: (columnKey: keyof HcpLandscapeRow | "full_name") => boolean;
}

/**
 * Centralized hook to determine which HCP columns should be visible
 * based on client configuration and indication
 */
export const useHcpLandscapeColumnVisibility = (
  client?: string | null,
  indication?: string | null | undefined,
  isFrm?: boolean,
  role?: string | null,
): HcpColumnVisibilityResult => {
  const hcpConfig = useClientHCPLandscapeConfiguration(client);

  const visibleColumns = useMemo(() => {
    if (!client) {
      return [];
    }

    // Helper function to get default columns based on profile
    const getDefaultColumns = (): (keyof HcpLandscapeRow | "full_name")[] => {
      const defaultVisibleColumns: (keyof HcpLandscapeRow | "full_name")[] = [
        "full_name",
        "fav_pct",
        "delta_fa",
        "acc_pct",
        "mkt_trx",
        "prod_trx",
        "prod_share",
      ];
      const defaultVisibleFrmColumns: (keyof HcpLandscapeRow | "full_name")[] = [
        "full_name",
        "fav_pct",
        "delta_fa",
        "acc_pct",
      ];

      return isFrm
        ? (hcpConfig?.visibleFrmColumns ?? defaultVisibleFrmColumns)
        : (hcpConfig?.visibleColumns ?? defaultVisibleColumns);
    };

    // Get base columns
    let baseColumns: (keyof HcpLandscapeRow | "full_name")[];

    const indicationFiltering = hcpConfig?.indicationFiltering;

    if (indicationFiltering && indication) {
      const indicationMappings = hcpConfig?.indicationColumnMappings;

      if (indicationMappings && indicationMappings[indication]) {
        baseColumns = indicationMappings[indication] as (keyof HcpLandscapeRow | "full_name")[];
      } else {
        baseColumns = getDefaultColumns();
      }
    } else {
      baseColumns = getDefaultColumns();
    }

    // Add role-specific columns if they exist
    if (role) {
      const roleAdditionalColumns = hcpConfig?.roleAdditionalColumns?.[role];
      if (roleAdditionalColumns && roleAdditionalColumns.length > 0) {
        return [...baseColumns, ...roleAdditionalColumns] as (keyof HcpLandscapeRow | "full_name")[];
      }
    }

    return baseColumns;
  }, [client, indication, hcpConfig, isFrm, role]);

  const isColumnVisible = useMemo(() => {
    return (columnKey: keyof HcpLandscapeRow | "full_name") => visibleColumns.includes(columnKey);
  }, [visibleColumns]);

  return {
    visibleColumns,
    isColumnVisible,
  };
};

// Define the HCP filter mapping type
type HcpFilterColumnMapping = {
  favorableAccess: keyof HcpLandscapeRow | "full_name";
  favorableAccessChange: keyof HcpLandscapeRow | "full_name";
  overallAccess: keyof HcpLandscapeRow | "full_name";
  marketTrx: keyof HcpLandscapeRow | "full_name";
  productTrx: keyof HcpLandscapeRow | "full_name";
  competitorProductTrx: keyof HcpLandscapeRow | "full_name";
  productShare: keyof HcpLandscapeRow | "full_name";
};

export interface HcpFilterVisibilityResult {
  isFilterVisible: (filterKey: keyof HcpFilterColumnMapping) => boolean;
  filterColumnMapping: HcpFilterColumnMapping;
}

/**
 * Hook specifically for HCP filter visibility in modals
 * Maps filter fields to their corresponding column keys
 */
export const useHcpLandscapeFilterVisibility = (
  client?: string | null,
  indication?: string | null | undefined,
  isFrm?: boolean,
): HcpFilterVisibilityResult => {
  const { isColumnVisible } = useHcpLandscapeColumnVisibility(client, indication, isFrm);

  // Mapping of filter fields to their corresponding column keys
  const filterColumnMapping: HcpFilterColumnMapping = useMemo(
    () => ({
      favorableAccess: "fav_pct",
      favorableAccessChange: "delta_fa",
      overallAccess: "acc_pct",
      marketTrx: "mkt_trx",
      productTrx: "prod_trx",
      competitorProductTrx: "comp_prod_trx",
      productShare: "prod_share",
    }),
    [],
  );

  const isFilterVisible = useMemo(() => {
    return (filterKey: keyof HcpFilterColumnMapping) => {
      const columnKey = filterColumnMapping[filterKey];
      return isColumnVisible(columnKey);
    };
  }, [isColumnVisible, filterColumnMapping]);

  return {
    isFilterVisible,
    filterColumnMapping,
  };
};
